import { supabase } from '@/integrations/supabase/client';
import { AppSettings, defaultAppSettings } from '@/types/appSettings';

/**
 * Get application settings
 */
export const getAppSettings = async (): Promise<AppSettings | null> => {
  try {
    console.log('Fetching app settings');
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .maybeSingle(); // Use maybeSingle instead of single to prevent errors if no data

    if (error) {
      console.error('Error fetching app settings:', error);
      throw error;
    }

    console.log('App settings fetched:', data);
    
    // Apply defaults if no data found
    if (!data) {
      return { ...defaultAppSettings };
    }
    
    return data as AppSettings;
  } catch (error) {
    console.error('Failed to get app settings:', error);
    throw error;
  }
};

/**
 * Update application settings
 */
export const updateAppSettings = async (settings: Partial<AppSettings>): Promise<AppSettings> => {
  try {
    // Log the settings being updated for debugging
    console.log('Updating app settings with:', settings);

    // Check if app_settings record exists
    const { count, error: countError } = await supabase
      .from('app_settings')
      .select('id', { count: 'exact' });

    if (countError) {
      console.error('Error checking if app_settings exists:', countError);
      throw countError;
    }

    let updateError;
    
    // If no records exist, insert a new one with ID 1
    if (count === 0) {
      // Start with default settings and apply provided updates
      const newSettings = { ...defaultAppSettings, ...settings };
      
      const { error } = await supabase
        .from('app_settings')
        .insert(newSettings);
      
      updateError = error;
    } else {
      // Otherwise update the existing record with ID 1
      const updateData: any = {
        updated_at: new Date().toISOString()
      };
      
      // Only include fields that are actually provided
      if (settings.app_name !== undefined) updateData.app_name = settings.app_name;
      if (settings.logo_url !== undefined) updateData.logo_url = settings.logo_url;
      if (settings.favicon_url !== undefined) updateData.favicon_url = settings.favicon_url;
      if (settings.social_preview_url !== undefined) updateData.social_preview_url = settings.social_preview_url;
      if (settings.games_allowed_per_day !== undefined) updateData.games_allowed_per_day = settings.games_allowed_per_day;
      
      console.log('Sending update with data:', updateData);
      
      const { error } = await supabase
        .from('app_settings')
        .update(updateData)
        .eq('id', 1);
      
      updateError = error;
    }

    if (updateError) {
      console.error('Error updating app settings:', updateError);
      throw updateError;
    }

    // Fetch the updated settings to return
    const updatedSettings = await getAppSettings();
    
    // Apply changes to document if relevant
    updateAppAssets(settings);
    
    return updatedSettings as AppSettings;
  } catch (error) {
    console.error('Failed to update app settings:', error);
    throw error;
  }
};

/**
 * Update app assets in the document
 */
const updateAppAssets = (settings: Partial<AppSettings>) => {
  // Update document title if app_name is provided
  if (settings.app_name) {
    document.title = settings.app_name;
  }
  
  // Update favicon if provided
  if (settings.favicon_url) {
    const faviconLink = document.querySelector("link[rel='icon']");
    if (faviconLink && faviconLink instanceof HTMLLinkElement) {
      faviconLink.href = settings.favicon_url;
    } else {
      // Create new favicon link if it doesn't exist
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = settings.favicon_url;
      document.head.appendChild(link);
    }
  }
  
  // Update app manifest if PWA is installed
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      navigator.serviceWorker.controller.postMessage({
        type: 'UPDATE_APP_ASSETS',
        data: {
          appName: settings.app_name,
          logoUrl: settings.logo_url,
          faviconUrl: settings.favicon_url,
          version: Date.now()
        }
      });
    } catch (e) {
      console.error('Error updating service worker assets:', e);
    }
  }
};
